//-- Copyright (c) 2021 TMT Insights Group, LLC
import { defaults } from 'chart.js';
import { typography } from '../../constants/baseThemes';

export const initializeChartjs = () => {
  defaults.font.family = typography?.fontFamily || 'Open Sans, sans-serif';
  defaults.font.size = 14;
  defaults.font.weight = 500;
  defaults.plugins.legend.position = 'bottom';
  defaults.plugins.legend.labels.usePointStyle = true;
  defaults.plugins.legend.labels.pointStyle = 'circle';
  defaults.plugins.legend.labels.padding = 30;
  defaults.plugins.tooltip.usePointStyle = true;
  defaults.plugins.tooltip.padding = 20;
};

export const CHART_COLORS = {
  default: 'default',
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
  teal: 'teal',
  tertiary: 'tertiary',
  pending: 'pending'
};

export const CHART_STATUS_LABELS = [
  {
    color: CHART_COLORS.default,
    filter: ['awaiting_submit'],
    ignoreOnPieChart: false,
    name: 'Awaiting Submit',
    status: 'awaiting_submit',
    icon: 'clock'
  },
  {
    color: CHART_COLORS.warning,
    filter: ['awaiting_assets'],
    name: 'Not Workable',
    ignoreOnPieChart: false,
    status: 'awaiting_assets',
    icon: 'timelapse'
  },
  {
    color: CHART_COLORS.pending,
    filter: ['processing'],
    name: 'Processing',
    ignoreOnPieChart: false,
    status: 'process_and_confirm',
    icon: 'clock_progress'
  },
  {
    color: CHART_COLORS.info,
    filter: ['awaiting_delivery'],
    name: 'Awaiting Delivery',
    ignoreOnPieChart: false,
    status: 'awaiting_delivery',
    icon: 'flight'
  },
  {
    color: CHART_COLORS.success,
    filter: ['delivered'],
    ignoreOnPieChart: true,
    name: 'Media Delivered',
    ignoreOnPieChart: false,
    status: 'media_delivered',
    icon: 'check'
  }
];

export const parseDashboardStats = (payload = {}, config, defaultConfig, jobStages) => {
  const { data } = payload;
  if (!data) return {};
  let parsedData = {};
  // get statuses from status card filters
  const statuses = {};

  // merge old statuses with new, so that if new is deleted it has old fallback
  const { statusCards } = { ...defaultConfig, statusCards: mergeNewStatusCardsWithDefault(config?.statusCards, defaultConfig?.statusCards, jobStages) };
  statusCards.forEach((sc) => {
    statuses[sc.status] = sc.filter;
  });

  // grab only dates, sometimes we get misc data in response
  var dateReg = /^\d{4}([./-])\d{1,2}\1\d{2}$/;
  let dates = Object.keys(data)?.filter((date) => !!date.match(dateReg));
  dates = dates.sort();

  // fill in missing months
  const newDates = [];

  dates.forEach((date, i) => {
    const splitDate = date.split('-');
    const year = Number(splitDate[0]),
      month = Number(splitDate[1]);
    const nextDate = dates[i + 1];
    newDates.push(date);

    if (nextDate) {
      const nextSplitDate = nextDate.split('-');
      const nextMonth = Number(nextSplitDate[1]);

      if (month !== 12 && nextMonth !== month + 1) {
        newDates.push(`${year}-${month + 1}-01`);
      } else if (month === 12 && nextMonth !== 1) {
        newDates.push(`${year + 1}-1-01`);
      }
    }
  });

  newDates.forEach((date) => {
    parsedData[date] = {};
    Object.keys(statuses).forEach((status) => {
      let total = 0;
      statuses[status].forEach((x) => (total += data[date]?.[x]?.total || 0));
      parsedData[date][status] = total || 0;
      parsedData[date].otd100 = data[date] ? data[date]?.on_time_complete / data[date]?.total : 0;
      parsedData[date].total = data[date]?.total || 0;

      // Check if valueKey exists and use it to access the nested field in data[date]
      if (statusCards.find((sc) => sc.status === status)?.valueKey) {
        const valueKey = statusCards.find((sc) => sc.status === status).valueKey;
        const keys = valueKey.split('.');
        const nestedValue = keys.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : null), data[date]);
        parsedData[date][valueKey] = nestedValue || 0;
      }
    });
  });

  Object.keys(parsedData).forEach((key) => {
    let newKey = key.split('-').map((x) => x.padStart(2, 0));
    newKey[2] = '02';
    newKey = newKey.join('-');
    parsedData[newKey] = parsedData[key];
    delete parsedData[key];
  });

  const parsedJobsByType = {};
  for (const date in payload.total_jobs_completed_by_type) {
    const adjustedDate = date.slice(0, date.length - 1) + 2;
    parsedJobsByType[adjustedDate] = payload.total_jobs_completed_by_type[date];
  }

  const tasksProgressMonthAvg = payload.tasks_progress_to_complete_monthly_average_length?.map((taskType) => {
    const transformedData = taskType.data.map((monthData) => ({
      ...monthData,
      date: monthData.date + '-02'
    }));

    transformedData.push({
      date: 'All',
      average_length_in_minutes: transformedData.reduce((acc, curr) => acc + curr.average_length_in_minutes, 0) / transformedData.length
    });

    return {
      ...taskType,
      data: transformedData
    };
  });

  return {
    data: { ...parsedData },
    parsedJobsByType: parsedJobsByType,
    tasksProgressMonthlyAvg: tasksProgressMonthAvg
  };
};

export const mergeNewStatusCardsWithDefault = (newStatusCards, defaultStatusCards, jobStages) => {
  const newStatuses = newStatusCards.map((card) => card.status);
  const mergedStatusCards = [...newStatusCards].concat(defaultStatusCards.filter((card) => !newStatuses.includes(card.status)));

  return mergedStatusCards.map((statusCard) => {
    return {
      ...statusCard,
      filter: statusCard.filter.filter((status) => !jobStages || jobStages?.includes(status))
    };
  });
};
